import { useState } from "react";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DatePicker } from "@/components/shared/date-picker";
import { SheetBase } from "@/components/shared/sheet-base";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNextNumberPreview } from "@/hooks/use-number-sequences";

import { SaleItemsTable } from "./sale-items-table";
import { SALE_CHANNEL_LABELS } from "./sale-labels";
import { SalePaymentSection } from "./sale-payment-section";
import { useSaleForm } from "./use-sale-form";

import type { Sale } from "@artisancode/api-types";

export function SaleDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: Sale | null;
}) {
  const {
    form,
    fields,
    products,
    stockByProductId,
    grandTotal,
    selectProduct,
    addLine,
    removeLine,
    onSubmit,
    isSubmitting,
  } = useSaleForm(open, editing, () => onOpenChange(false));
  const { preview: sequencePreview, autoGenerate } =
    useNextNumberPreview("sale");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const title = editing ? "Edit Penjualan" : "Tambah Penjualan";

  const footer = (
    <>
      <Button
        variant="outline"
        onClick={() => onOpenChange(false)}
        disabled={isSubmitting}
      >
        Batal
      </Button>
      <Button type="submit" form="sale-form" disabled={isSubmitting}>
        {isSubmitting ? "Menyimpan..." : editing ? "Simpan" : "Tambah"}
      </Button>
    </>
  );

  const formContent = (
    <Form {...form}>
      <form
        id="sale-form"
        onSubmit={form.handleSubmit(() =>
          editing ? onSubmit() : setConfirmOpen(true),
        )}
        className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto py-2 pr-1 md:grid-cols-[1.4fr_1fr]"
      >
        <div className="space-y-3 md:flex md:min-h-0 md:flex-col md:gap-3 md:space-y-0">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tanggal</FormLabel>
                  <FormControl>
                    <DatePicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="channel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Channel</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(SALE_CHANNEL_LABELS).map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <SaleItemsTable
            form={form}
            fields={fields}
            products={products}
            stockByProductId={stockByProductId}
            grandTotal={grandTotal}
            selectProduct={selectProduct}
            addLine={addLine}
            removeLine={removeLine}
            locked={editing?.hasReceivablePayments}
          />
        </div>

        <SalePaymentSection
          form={form}
          editing={editing}
          sequencePreview={sequencePreview}
          autoGenerate={autoGenerate}
        />
      </form>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Simpan penjualan?"
        description="Bahan resep di bar (atau stok produk beli) langsung berkurang dan tercatat di Kartu Stok."
        confirmLabel="Ya, Simpan"
        variant="default"
        isConfirming={isSubmitting}
        onConfirm={async () => {
          await onSubmit();
          setConfirmOpen(false);
        }}
      />
    </Form>
  );

  return (
    <SheetBase
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      className="sm:max-w-5xl"
      scrollable={false}
      footer={footer}
    >
      {formContent}
    </SheetBase>
  );
}
